
import { supabase } from '@/integrations/supabase/client';

/**
 * Mark certificates as reported when included in a report
 */
export async function markCertificatesAsReported(
  certificateNumbers: string[],
  reportId: string
): Promise<{ success: boolean; error: any }> {
  try {
    // Update all matching certificate numbers
    const { error } = await supabase
      .from('certificate_number_inventory')
      .update({
        reporting_status: 'reported',
        report_id: reportId,
        report_date: new Date().toISOString(),
        reported: true,
        reported_at: new Date().toISOString()
      })
      .in('certificate_number', certificateNumbers);
      
    if (error) throw error;
    
    return { success: true, error: null };
  } catch (error) {
    console.error("Error marking certificates as reported:", error);
    return { success: false, error };
  }
}

/**
 * Identifies certificates that have been used but not reported
 */
export async function findUnreportedCertificates(
  state: string,
  startDate: string,
  endDate: string
): Promise<{ data: any[]; error: any }> {
  try {
    const { data, error } = await supabase
      .from('certificate_number_inventory')
      .select('*')
      .eq('state', state)
      .eq('is_used', true)
      .eq('reporting_status', 'unreported')
      .gte('assigned_at', startDate)
      .lte('assigned_at', endDate);
      
    if (error) throw error;
    
    return { data: data || [], error: null };
  } catch (error) {
    console.error("Error finding unreported certificates:", error);
    return { data: [], error };
  }
}

/**
 * Gets reporting statistics for certificates
 */
export async function getCertificateReportingStats(
  state: string,
  year: number,
  quarter: number
): Promise<{ data: any; error: any }> {
  try {
    // Calculate quarter date range
    const startMonth = (quarter - 1) * 3;
    const startDate = new Date(year, startMonth, 1);
    const endDate = new Date(year, startMonth + 3, 0);
    
    // Format for PostgreSQL
    const formattedStartDate = startDate.toISOString();
    const formattedEndDate = endDate.toISOString();
    
    // Get total used certificates in this period
    const { data: totalUsed, error: totalError } = await supabase
      .from('certificate_number_inventory')
      .select('id', { count: 'exact' })
      .eq('state', state)
      .eq('is_used', true)
      .gte('assigned_at', formattedStartDate)
      .lte('assigned_at', formattedEndDate);
      
    if (totalError) throw totalError;
    
    // Get reported certificates in this period
    const { data: totalReported, error: reportedError } = await supabase
      .from('certificate_number_inventory')
      .select('id', { count: 'exact' })
      .eq('state', state)
      .eq('is_used', true)
      .eq('reporting_status', 'reported')
      .gte('assigned_at', formattedStartDate)
      .lte('assigned_at', formattedEndDate);
      
    if (reportedError) throw reportedError;
    
    // Calculate percentages and return stats
    const totalUsedCount = totalUsed?.length || 0;
    const totalReportedCount = totalReported?.length || 0;
    const percentReported = totalUsedCount > 0 
      ? Math.round((totalReportedCount / totalUsedCount) * 100) 
      : 0;
    
    return {
      data: {
        totalUsed: totalUsedCount,
        totalReported: totalReportedCount,
        percentReported,
        unreported: totalUsedCount - totalReportedCount
      },
      error: null
    };
  } catch (error) {
    console.error("Error getting certificate reporting stats:", error);
    return { data: null, error };
  }
}

/**
 * Verify a certificate during the reporting process
 */
export async function verifyCertificate(
  certificateId: string,
  status: string,
  notes: string = ''
): Promise<{ success: boolean; error: any }> {
  try {
    // Get the current user
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    
    // Update the certificate inventory record
    const { error } = await supabase
      .from('certificate_number_inventory')
      .update({
        verification_status: status,
        verification_date: new Date().toISOString(),
        verified_by: userId
      })
      .eq('id', certificateId);
      
    if (error) throw error;
    
    // Get certificate details
    const { data: certData, error: certError } = await supabase
      .from('certificate_number_inventory')
      .select('certificate_number, report_id')
      .eq('id', certificateId)
      .single();
      
    if (certError) throw certError;
    
    // Create verification history record
    const { error: historyError } = await supabase
      .from('certificate_verification_history')
      .insert({
        certificate_number: certData?.certificate_number,
        inventory_id: certificateId,
        report_id: certData?.report_id,
        verification_status: status,
        verified_by: userId,
        notes
      });
      
    if (historyError) throw historyError;
    
    return { success: true, error: null };
  } catch (error) {
    console.error("Error verifying certificate:", error);
    return { success: false, error };
  }
}
